'use client'

import { useState, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { RefreshCw, Eye, EyeOff } from 'lucide-react'
import Image from 'next/image'
import platformLogo from '@/public/logo.png'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import Api from '@/api/h5Api'

interface ApiResponse<T = any> {
  code: number
  message?: string
  data: T
}

interface ForgotPasswordDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onLoginClick?: () => void
}

export function ForgotPasswordDialog({
  open,
  onOpenChange,
  onLoginClick,
}: ForgotPasswordDialogProps) {
  const router = useRouter()
  const [isLoading, setIsLoading] = useState(false)
  const [resetError, setResetError] = useState<string | null>(null)

  // 表单状态
  const [email, setEmail] = useState('')
  const [emailId, setEmailId] = useState('')
  const [emailCode, setEmailCode] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [code, setCode] = useState('')
  const [captchaId, setCaptchaId] = useState('')
  const [captcha, setCaptcha] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  // 邮箱验证码倒计时
  const [countdown, setCountdown] = useState(0)
  const [isSendingCode, setIsSendingCode] = useState(false)

  // 验证邮箱格式
  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  // 获取图片验证码
  const getCaptcha = async () => {
    try {
      const res = (await Api.userCaptcha({})) as unknown as ApiResponse
      if (res.code === 200) {
        setCaptcha(res.data.captcha)
        setCaptchaId(res.data.captchaId)
      }
    } catch (error) {
      console.error('获取验证码失败:', error)
    }
  }

  // 发送邮箱验证码
  const handleSendEmailCode = async () => {
    setResetError(null)

    if (email.trim() === '') {
      setResetError('邮箱不能为空')
      return
    }

    if (!validateEmail(email)) {
      setResetError('邮箱格式不正确')
      return
    }

    if (code.trim() === '') {
      setResetError('图片验证码不能为空')
      return
    }

    setIsSendingCode(true)
    try {
      const res = (await Api.userSendEmailCode(
        { username: email, type: 'forget', code, captchaId, isForgetPassword: true },
      )) as unknown as ApiResponse

      if (res.code === 200) {
        setCountdown(60)
        setResetError(null)
        setEmailId(res.data.emailId)
      }
    } catch (error: any) {
      console.error('发送验证码失败:', error)
      setResetError(error?.message || '发送验证码失败，请稍后重试')
      getCaptcha() // 重新获取图片验证码
    } finally {
      setIsSendingCode(false)
    }
  }

  // 处理重置密码
  const handleResetPassword = async () => {
    setResetError(null)

    if (email.trim() === '') {
      setResetError('邮箱不能为空')
      return
    }

    if (!validateEmail(email)) {
      setResetError('邮箱格式不正确')
      return
    }

    if (code.trim() === '') {
      setResetError('图片验证码不能为空')
      return
    }

    if (emailCode.trim() === '') {
      setResetError('邮箱验证码不能为空')
      return
    }

    if (newPassword.trim() === '') {
      setResetError('新密码不能为空')
      return
    }

    if (newPassword.length < 6 || newPassword.length > 20) {
      setResetError('密码长度应在6-20个字符之间')
      return
    }

    setIsLoading(true)
    try {
      const res = (await Api.userEditPassword(
        {
          username: email,
          emailCode,
          password: newPassword,
          code,
          captchaId,
          emailId
        },
      )) as unknown as ApiResponse

      if (res.code === 200) {
        // 密码重置成功，关闭对话框并跳转到登录
        onOpenChange(false)
        onLoginClick?.()
      }
    } catch (error: any) {
      console.error('重置密码失败:', error)
      setResetError(error?.message || '重置密码失败，请稍后重试')
      getCaptcha() // 重新获取图片验证码
    } finally {
      setIsLoading(false)
    }
  }

  // Enter 键提交
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isLoading) {
      handleResetPassword()
    }
  }

  // 倒计时效果
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1)
      }, 1000)
      return () => clearTimeout(timer)
    }
  }, [countdown])

  // 当对话框打开时获取验证码
  useEffect(() => {
    if (open) {
      getCaptcha()
    } else {
      // 关闭时重置表单
      setEmail('')
      setEmailCode('')
      setNewPassword('')
      setCode('')
      setShowPassword(false)
      setResetError(null)
      setCountdown(0)
    }
  }, [open])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-center">
            <div className="flex items-center justify-center gap-3 mb-2">
              <div className="relative w-10 h-10 rounded-xl overflow-hidden">
                <Image
                  src={platformLogo}
                  alt="AI智能学习平台"
                  fill
                  className="object-contain"
                />
              </div>
              <h1 className="text-lg font-bold text-primary">AI智能学习平台</h1>
            </div>
            重置您的密码
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Error Alert */}
          {resetError && (
            <Alert variant="destructive">
              <AlertDescription>{resetError}</AlertDescription>
            </Alert>
          )}

          {/* Email Input */}
          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm font-medium">
              邮箱
            </Label>
            <Input
              id="email"
              type="email"
              placeholder="请输入邮箱"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={handleKeyDown}
              className="focus:ring-primary focus:border-primary"
              autoFocus
            />
          </div>

          {/* Image Captcha Input */}
          <div className="space-y-2">
            <Label htmlFor="code" className="text-sm font-medium">
              图片验证码
            </Label>
            <div className="flex gap-2">
              <Input
                id="code"
                type="text"
                placeholder="请输入验证码"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                onKeyDown={handleKeyDown}
                maxLength={6}
                className="focus:ring-primary focus:border-primary flex-1"
              />
              <button
                onClick={getCaptcha}
                className="shrink-0 w-28 h-10 rounded-lg bg-muted hover:bg-muted/80 border border-border transition-all duration-300 overflow-hidden flex items-center justify-center"
                dangerouslySetInnerHTML={{ __html: captcha }}
              />
            </div>
          </div>

          {/* Email Code Input */}
          <div className="space-y-2">
            <Label htmlFor="emailCode" className="text-sm font-medium">
              邮箱验证码
            </Label>
            <div className="flex gap-2">
              <Input
                id="emailCode"
                type="text"
                placeholder="请输入验证码"
                value={emailCode}
                onChange={(e) => setEmailCode(e.target.value)}
                onKeyDown={handleKeyDown}
                maxLength={6}
                className="focus:ring-primary focus:border-primary flex-1"
              />
              <Button
                type="button"
                onClick={handleSendEmailCode}
                disabled={countdown > 0 || isSendingCode}
                variant="outline"
                className="shrink-0 min-w-25"
              >
                {isSendingCode ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : countdown > 0 ? (
                  `${countdown}s`
                ) : (
                  '获取验证码'
                )}
              </Button>
            </div>
          </div>

          {/* New Password Input */}
          <div className="space-y-2">
            <Label htmlFor="newPassword" className="text-sm font-medium">
              新密码
            </Label>
            <div className="relative">
              <Input
                id="newPassword"
                type={showPassword ? 'text' : 'password'}
                placeholder="请输入新密码（6-20个字符）"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                onKeyDown={handleKeyDown}
                maxLength={20}
                className="focus:ring-primary focus:border-primary pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors duration-300"
              >
                {showPassword ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>

          {/* Reset Button */}
          <Button
            onClick={handleResetPassword}
            className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-medium"
            disabled={isLoading}
          >
            {isLoading ? (
              <span className="flex items-center gap-2">
                <RefreshCw className="w-4 h-4 animate-spin" />
                重置中...
              </span>
            ) : (
              '重置密码'
            )}
          </Button>

          {/* Login Link */}
          <div className="flex items-center justify-center pt-2 text-sm border-t">
            <span className="text-muted-foreground">想起密码了？</span>
            <button
              className="text-primary hover:text-primary/80 transition-colors ml-1 font-medium"
              onClick={() => {
                onOpenChange(false)
                onLoginClick?.()
              }}
            >
              返回登录
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
